/**
 * VehiclePickerGrid — « le parc », côté conducteur.
 *
 * Remplace la liste verticale d'origine : sur un parc de 15 véhicules elle
 * s'étirait en ascenseur (retour JGS, test Chrome du 2026-09-13, un 13e
 * véhicule inatteignable sans scroller). Une grille de cartes compactes tient
 * un parc de cette taille sur un écran de portable ; à 1 500 véhicules,
 * c'est le filtre catégorie et la recherche plaque/modèle qui font le travail,
 * pas un tri manuel dans une longue colonne.
 *
 * Ne recalcule rien : `vehicles` est la réponse telle quelle de
 * `poolAPI.getAvailability` (`frontend/src/api/pool.js`) — même champs,
 * même `unavailable_reason`. Ce composant ne fait que la présenter,
 * la filtrer et la trier côté client.
 */
import React, { useMemo, useState } from 'react';
import { Car, Search, X, Fuel, Gauge, AlertTriangle, Clock } from 'lucide-react';

/**
 * Kilométrage de la carte — retour JGS (test Chrome 2026-09-13). Le chiffre vient
 * de `currentOdometer`, le même que le préremplissage de la prise : la carte et le
 * formulaire ne peuvent pas se contredire. `vehicle_mileage` est un chiffre figé
 * entre deux pleins (pool_service.mjs, règle 3) : on le montre « ≈ », jamais comme
 * une lecture. Source inconnue : rien, plutôt qu'un 0 inventé.
 */
const LIVE_ODOMETER_SOURCES = ['telematics', 'shadow', 'reading'];

function OdometerLine({ odometer, t }) {
  if (odometer?.km == null) return null;
  const km = Number(odometer.km).toLocaleString();
  if (LIVE_ODOMETER_SOURCES.includes(odometer.source)) {
    return <span className="flex items-center gap-1"><Gauge className="h-3 w-3" /> {t('poolDriver.picker.odometer', { km })}</span>;
  }
  if (odometer.source === 'vehicle_mileage') {
    return (
      <span className="flex items-center gap-1" title={t('poolDriver.picker.odometerStaleHint')}>
        <Gauge className="h-3 w-3" /> {t('poolDriver.picker.odometerStale', { km })}
      </span>
    );
  }
  return null;
}

/**
 * Carburant — « ce qui attend le conducteur » (retour JGS du 2026-09-13). Le niveau
 * est celui DÉCLARÉ à la dernière passation, jamais une lecture : le libellé le dit et
 * l'infobulle porte la date. Rien de déclaré : on le dit, on ne dessine pas une jauge vide.
 */
const FUEL_STEPS = [0, 25, 50, 75, 100];

function FuelLine({ lastFuel, t }) {
  if (!lastFuel || lastFuel.pct == null) {
    return <span className="flex items-center gap-1 text-gray-400"><Fuel className="h-3 w-3" /> {t('poolDriver.picker.fuelUnknown')}</span>;
  }
  const pct = Math.max(0, Math.min(100, Number(lastFuel.pct)));
  const label = FUEL_STEPS.includes(pct) ? t(`poolDriver.handover.fuelSteps.${pct}`) : `${pct} %`;
  const date = lastFuel.at ? new Date(lastFuel.at).toLocaleDateString() : '';
  const hint = t(lastFuel.direction === 'checkout' ? 'poolDriver.picker.fuelDeclaredCheckout' : 'poolDriver.picker.fuelDeclaredCheckin', { date });
  const low = pct <= 25;
  return (
    <span className="flex items-center gap-1.5" title={hint} aria-label={`${t('poolDriver.handover.fuel')} : ${label} — ${hint}`}>
      <Fuel className={`h-3 w-3 ${low ? 'text-amber-600' : ''}`} />
      <span className="flex h-2 w-12 overflow-hidden rounded-full bg-gray-200" aria-hidden="true">
        <span className={`h-full ${low ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
      </span>
      <span className={low ? 'font-medium text-amber-700' : ''}>{label}</span>
    </span>
  );
}

/** Libellé lisible d'une catégorie ; un code inconnu reste affiché tel quel. */
const classLabel = (code, t) => t(`poolDriver.picker.vehicleClass.${code}`, { defaultValue: code });

/** Disponibles d'abord (retour JGS) ; à égalité, plaque triée. */
function sortVehicles(list) {
  return [...list].sort((a, b) => {
    const aFree = a.available !== false && !a.unavailable_reason;
    const bFree = b.available !== false && !b.unavailable_reason;
    if (aFree !== bFree) return aFree ? -1 : 1;
    return (a.license_plate || '').localeCompare(b.license_plate || '');
  });
}

function VehicleTile({ v, canBook, booking, onChoose, t }) {
  const free = v.available !== false && !v.unavailable_reason;
  const reasonKey = v.unavailable_reason ? `poolDriver.picker.reason.${v.unavailable_reason}` : null;
  // Défauts qui n'empêchent pas de rouler, mais que le conducteur doit connaître avant de choisir.
  const signaledDefects = Math.max(0, (v.open_defect_count || 0) - (v.blocking_defects?.length || 0));
  const nextAt = free && v.next_reservation_at
    ? new Date(v.next_reservation_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <div className={`flex flex-col gap-2 rounded-xl border p-3 ${free ? 'border-gray-200 bg-white' : 'border-gray-200 bg-gray-50'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-lg font-bold text-gray-900">{v.license_plate || `#${v.vehicle_id}`}</p>
          <p className="truncate text-sm text-gray-600">
            {[v.make, v.model].filter(Boolean).join(' ') || t('poolDriver.reservation.vehicle')}
          </p>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
            free ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
          }`}
        >
          {free ? t('poolDriver.picker.state.available') : t(reasonKey, v.unavailable_reason)}
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
        {v.vehicle_class && (
          <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-700">{classLabel(v.vehicle_class, t)}</span>
        )}
        <OdometerLine odometer={v.odometer} t={t} />
        {v.fuel_type && <span>{v.fuel_type}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
        <FuelLine lastFuel={v.last_fuel} t={t} />
        {signaledDefects > 0 && (
          <span className="flex items-center gap-1 font-medium text-amber-700">
            <AlertTriangle className="h-3 w-3" /> {t('poolDriver.picker.defectsSignaled', { count: signaledDefects })}
          </span>
        )}
        {nextAt && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" /> {t('poolDriver.picker.nextReservation', { time: nextAt })}
          </span>
        )}
      </div>

      <button
        onClick={() => onChoose(v.vehicle_id || v.id)}
        disabled={!canBook || !free || booking}
        title={!canBook ? t('poolDriver.picker.bookDisabledHint') : undefined}
        className="mt-auto min-h-[40px] w-full rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
      >
        {booking ? t('poolDriver.search.booking') : free ? t('poolDriver.search.choose') : t('poolDriver.search.unavailable')}
      </button>
    </div>
  );
}

function VehiclePickerGrid({ vehicles, loading, canBook, booking, onChoose, t }) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const categories = useMemo(
    () => [...new Set((vehicles || []).map((v) => v.vehicle_class).filter(Boolean))].sort(),
    [vehicles]
  );

  const filtered = useMemo(() => {
    let list = vehicles || [];
    if (category !== 'all') list = list.filter((v) => v.vehicle_class === category);
    if (query.trim().length >= 1) {
      const q = query.trim().toLowerCase();
      list = list.filter((v) =>
        (v.license_plate || '').toLowerCase().includes(q) ||
        (v.make || '').toLowerCase().includes(q) ||
        (v.model || '').toLowerCase().includes(q));
    }
    return sortVehicles(list);
  }, [vehicles, category, query]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('poolDriver.picker.searchPlaceholder')}
            className="w-full rounded-lg border border-gray-300 py-2 pl-8 pr-7 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {categories.length > 1 && (
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="rounded-lg border border-gray-300 px-2.5 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          >
            <option value="all">{t('poolDriver.picker.categoryAll')}</option>
            {categories.map((c) => (
              <option key={c} value={c}>{classLabel(c, t)}</option>
            ))}
          </select>
        )}
      </div>

      {loading ? (
        <p className="py-6 text-center text-sm text-gray-500">{t('poolDriver.search.searching')}</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg bg-gray-50 p-6 text-center">
          <Car className="mx-auto mb-2 h-6 w-6 text-gray-300" />
          <p className="text-sm font-medium text-gray-700">{t('poolDriver.search.empty')}</p>
          <p className="mt-0.5 text-xs text-gray-500">{t('poolDriver.search.emptyHint')}</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500">{t('poolDriver.search.results', {
            count: filtered.filter((v) => v.available !== false && !v.unavailable_reason).length,
            total: filtered.length,
          })}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {filtered.map((v) => (
              <VehicleTile
                key={v.vehicle_id || v.id}
                v={v}
                canBook={canBook}
                booking={booking}
                onChoose={onChoose}
                t={t}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default VehiclePickerGrid;
